import { prettyEventValue, prettyJurisdiction, prettyPath, prettyScope } from './helpers';
import { translate, translationExists } from '/b2b/common/helpers/i18n';

import { startCase } from 'lodash';

const labelFor = field => {
    const key = `activityLog.fields.${field}`;
    return translationExists(key) ? translate(key) : startCase(String(field).split('.').pop());
};

// Enum values resolve by convention (activityLog.values.<kind>.<NAME>); a
// missing key degrades to a humanized canonical name, never the raw constant.
const enumValue = (kind, value) => {
    const name = value ?? 'DEFAULT';
    const key = `activityLog.values.${kind}.${name}`;
    return translationExists(key) ? translate(key) : startCase(String(name).toLowerCase());
};

const text = value => (value == null ? translate('common.empty') : String(value));

const list = (value, mapItem = String) => {
    if (value == null) return translate('common.empty');
    if (!Array.isArray(value)) return text(value);
    return value.length === 0 ? translate('common.empty') : value.map(mapItem).join(', ');
};

const VALUE_FORMATTERS = {
    boolean: value =>
        value == null
            ? translate('common.empty')
            : translate(value ? 'common.true' : 'common.false'),
    number: text,
    text,
    localizedText: text,
    color: text,
    stringList: value => list(value),
    jurisdictionList: value => list(value, prettyJurisdiction),
    usBannerVariant: value => enumValue('usBannerVariant', value),
    doNotSellCategoryList: value =>
        list(value, category => enumValue('doNotSellCategory', category)),
};

const SCOPE_RESOLVERS = {
    usJurisdiction: prettyJurisdiction,
    language: value => String(value).toUpperCase(),
};

const describeV2 = ({ field, valueKind, scope, before, after }) => {
    const formatValue = VALUE_FORMATTERS[valueKind];
    const resolveScope = scope && SCOPE_RESOLVERS[scope.kind];
    // An unknown kind or scope comes from a newer producer; let v1 render it.
    if (!formatValue || (scope && !resolveScope)) {
        return undefined;
    }

    return {
        label: labelFor(field),
        scope: scope ? resolveScope(scope.value) : undefined,
        before: formatValue(before),
        after: formatValue(after),
    };
};

export const describeChange = (metadata = {}) => {
    const { path, before, after, changeType, semantic } = metadata;
    const described = semantic ? describeV2(semantic) : undefined;

    return (
        described ?? {
            label: prettyPath(path, changeType),
            scope: prettyScope(path),
            before: prettyEventValue(before, path),
            after: prettyEventValue(after, path),
        }
    );
};
